import React, { useEffect } from 'react'
import { FormikErrors, FormikProps } from 'formik'
import { ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import { ActionButton } from 'uiComponents/buttons'
import classNames from 'classnames'
import { PricingPlan } from '../../types'
import { PricingPlanFormValues, TWO_YEAR_TERM, ONE_YEAR_TERM, getYearlyPrice } from '../../pricingPlanFormSchema'
import { PricingPlanElement } from '../../pricingPlan'
import { CheckboxFieldFormik, NumberInput, NumberInputProps } from 'uiComponents/input'
import { FormItem, WrapWithSymbol } from 'uiComponents/form/formElements'
import { useCurrency } from 'uiComponents/money'
import NumberInputFieldFormik, { NumberInputFormikProps } from 'uiComponents/input/numberInput/numberInputFieldFormik'
import SliderInput from 'uiComponents/input/slider/sliderInput'
import { CancelButton } from 'uiComponents/buttons/cancelButton'
import { RadioOptionTile } from 'uiComponents/optionTile'
import { useHasFeature } from 'features/hocs'
import './installments.scss'

const MoneyInput = (props: NumberInputProps) => {
    const currencySymbol = useCurrency().symbol
    return (
        <WrapWithSymbol symbol={currencySymbol} position="left">
            <NumberInput {...props} />
        </WrapWithSymbol>
    )
}

const MoneyInputFormik = (props: NumberInputFormikProps) => {
    const currencySymbol = useCurrency().symbol
    return (
        <WrapWithSymbol symbol={currencySymbol} position="left">
            <NumberInputFieldFormik {...props} />
        </WrapWithSymbol>
    )
}

const getSuggestedPrice = (oldPrice: number) => oldPrice + Math.round(oldPrice / 10)

export const TWO_YEAR_PLANS_FEATURE = 'TwoYearMembershipPlans'

interface ContractLengthProps {
    minimumTerm: string
    onTermChange: (term: string) => void
}

const ContractLength = ({ minimumTerm, onTermChange }: ContractLengthProps) => (
    <div className="contract-length">
        <label className="field-label">Contract length</label>
        <div className="contract-length-options">
            <RadioOptionTile
                title="1 year"
                description="Members commit to 1 year."
                checked={minimumTerm === ONE_YEAR_TERM}
                onClick={() => onTermChange(ONE_YEAR_TERM)}
            />
            <RadioOptionTile
                title="2 years"
                description="Members commit to 2 years and pay the same price each year."
                checked={minimumTerm === TWO_YEAR_TERM}
                onClick={() => onTermChange(TWO_YEAR_TERM)}
            />
        </div>
    </div>
)

interface AnnualPlanProps {
    allowYearlyInstallments: boolean
    suggestedPrice: number
}

const AnnualPlan = ({ allowYearlyInstallments, suggestedPrice }: AnnualPlanProps) => (
    <div className="annual-plan">
        <label className="payment-plan-header" htmlFor="allowYearlyInstallments">
            <CheckboxFieldFormik name="allowYearlyInstallments" id="allowYearlyInstallments" />
            <span className="field-label">Annual plan</span>
            <span>Enable payment for the full year upfront.</span>
        </label>
        <div
            className={classNames('collapsible', { expanded: allowYearlyInstallments })}
            {...({ inert: !allowYearlyInstallments || undefined } as any)}
        >
            <div className="collapsible-content">
                <div className="annual-total-price-wrapper">
                    <label className="field-label">Total price</label>
                    <FormItem className="annual-total-price">
                        <MoneyInputFormik
                            name="totalPriceYearly"
                            min={0}
                            maxLength={7}
                            placeholder={suggestedPrice.toFixed(2)}
                            autoCorrectInput={false}
                            resetToZero={false}
                        />
                    </FormItem>
                </div>
            </div>
        </div>
    </div>
)

interface PerYearPlanProps {
    allowPerYearPayment: boolean
    suggestedPrice: number
}

const PerYearPlan = ({ allowPerYearPayment, suggestedPrice }: PerYearPlanProps) => (
    <div className="per-year-plan">
        <label className="payment-plan-header" htmlFor="allowPerYearPayment">
            <CheckboxFieldFormik name="allowPerYearPayment" id="allowPerYearPayment" />
            <span className="field-label">Pay per year</span>
            <span>Members pay once at the start of each year.</span>
        </label>
        <div
            className={classNames('collapsible', { expanded: allowPerYearPayment })}
            {...({ inert: !allowPerYearPayment || undefined } as any)}
        >
            <div className="collapsible-content">
                <div className="annual-total-price-wrapper">
                    <label className="field-label">Price per year</label>
                    <FormItem className="annual-total-price">
                        <MoneyInputFormik
                            name="pricePerYear"
                            min={0}
                            maxLength={7}
                            placeholder={suggestedPrice.toFixed(2)}
                            autoCorrectInput={false}
                            resetToZero={false}
                        />
                    </FormItem>
                </div>
            </div>
        </div>
    </div>
)

interface UpfrontPlanProps {
    allowUpfrontPayment: boolean
    suggestedPrice: number
}

const UpfrontPlan = ({ allowUpfrontPayment, suggestedPrice }: UpfrontPlanProps) => (
    <div className="upfront-plan">
        <label className="payment-plan-header" htmlFor="allowUpfrontPayment">
            <CheckboxFieldFormik name="allowUpfrontPayment" id="allowUpfrontPayment" />
            <span className="field-label">Pay all at once</span>
            <span>Members pay the full 2 years in one payment.</span>
        </label>
        <div
            className={classNames('collapsible', { expanded: allowUpfrontPayment })}
            {...({ inert: !allowUpfrontPayment || undefined } as any)}
        >
            <div className="collapsible-content">
                <div className="annual-total-price-wrapper">
                    <label className="field-label">Total price for 2 years</label>
                    <FormItem className="annual-total-price">
                        <MoneyInputFormik
                            name="priceUpfront"
                            min={0}
                            maxLength={7}
                            placeholder={suggestedPrice.toFixed(2)}
                            autoCorrectInput={false}
                            resetToZero={false}
                        />
                    </FormItem>
                </div>
            </div>
        </div>
    </div>
)

interface MonthlyPlanProps {
    allowMonthlyInstallments: boolean
    suggestedPrice: number
    totalPriceMonthly: number
    initialPayment: number
    averageMonthlyPrice: number
    monthlyInstallment: number
    hasValidTotalPriceMonthly: boolean
    onInitialPaymentChange: (value: number) => void
    onMonthlyInstallmentChange: (value: number) => void
    errors: FormikErrors<PricingPlanFormValues>
}

const MonthlyPlan = ({
    allowMonthlyInstallments,
    suggestedPrice,
    totalPriceMonthly,
    initialPayment,
    averageMonthlyPrice,
    monthlyInstallment,
    hasValidTotalPriceMonthly,
    onInitialPaymentChange,
    onMonthlyInstallmentChange,
    errors,
}: MonthlyPlanProps) => {
    return (
        <div className="monthly-plan">
            <label className="payment-plan-header" htmlFor="allowMonthlyInstallments">
                <CheckboxFieldFormik name="allowMonthlyInstallments" id="allowMonthlyInstallments" />
                <span className="field-label">Monthly plan</span>
                <span>Enable payment in monthly installments.</span>
            </label>
            <div
                className={classNames('collapsible', { expanded: allowMonthlyInstallments })}
                {...({ inert: !allowMonthlyInstallments || undefined } as any)}
            >
                <div className="collapsible-content">
                    <div className="monthly-total-price-wrapper">
                        <label className="field-label">Total price</label>
                        <FormItem className="monthly-total-price">
                            <MoneyInputFormik
                                name="totalPriceMonthly"
                                min={0}
                                maxLength={7}
                                placeholder={suggestedPrice.toFixed(2)}
                                autoCorrectInput={false}
                                resetToZero={false}
                            />
                        </FormItem>
                    </div>
                    <div className="monthly-installments">
                        <div className="monthly-installments-row">
                            <label className="field-label">Initial payment</label>
                            <div />
                            <label className="field-label">Per month</label>
                        </div>
                        <div className="monthly-installments-row">
                            <MoneyInputFormik
                                name="initialPayment"
                                maxLength={7}
                                min={averageMonthlyPrice}
                                max={totalPriceMonthly}
                                placeholder={(suggestedPrice / 12).toFixed(2)}
                                step={1}
                                resetToZero={false}
                            />
                            <div className="monthly-installment-slider">
                                <SliderInput
                                    name="initial-payment"
                                    min={-totalPriceMonthly + Math.min(11, averageMonthlyPrice)}
                                    max={-averageMonthlyPrice}
                                    step={5}
                                    restrictSteps={true}
                                    value={-initialPayment}
                                    onChange={(e, value) => onInitialPaymentChange(-Number(value))}
                                    disabled={!hasValidTotalPriceMonthly}
                                />
                            </div>
                            <MoneyInput
                                name="monthlyInstallment"
                                value={monthlyInstallment || ''}
                                maxLength={7}
                                min={0}
                                max={averageMonthlyPrice}
                                placeholder={(suggestedPrice / 12).toFixed(2)}
                                step={1}
                                resetToZero={false}
                                className={classNames({ error: errors.initialPayment })}
                                onChange={(e) => onMonthlyInstallmentChange(Number(e.target.value))}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

interface PricingPlanInstallmentsProps {
    formik: FormikProps<PricingPlanFormValues>
    visible: boolean
    activePricingPlan: PricingPlan | null
    onClose: () => void
    onNextClick: () => void
}

export const PricingPlanInstallments = ({
    formik,
    visible,
    activePricingPlan,
    onClose,
    onNextClick,
}: PricingPlanInstallmentsProps) => {
    const oldYearlyPlan = activePricingPlan?.paymentPlans.find((plan) => plan.paymentInterval === 'P1Y')
    const oldMonthlyPlan = activePricingPlan?.paymentPlans.find((plan) => plan.paymentInterval === 'P1M')
    const oldUpfrontPlan = activePricingPlan?.paymentPlans.find((plan) => plan.paymentInterval === 'P2Y')

    const suggestedTotalPriceYearly = oldYearlyPlan ? getSuggestedPrice(getYearlyPrice(oldYearlyPlan)) : 200
    const suggestedTotalPriceMonthly = oldMonthlyPlan
        ? getSuggestedPrice(oldMonthlyPlan.totalPrice)
        : getSuggestedPrice(suggestedTotalPriceYearly) || 240

    const values = formik.values
    const isTwoYearTerm = values.minimumTerm === TWO_YEAR_TERM
    const hasTwoYearPlansFeature = useHasFeature(TWO_YEAR_PLANS_FEATURE)
    // Keep the selector visible on a plan that is already two-year, so it
    // stays editable even if the feature is later turned off for the account.
    const showContractLength = hasTwoYearPlansFeature || isTwoYearTerm

    const suggestedPriceUpfront = oldUpfrontPlan
        ? getSuggestedPrice(oldUpfrontPlan.totalPrice)
        : (values.pricePerYear || suggestedTotalPriceYearly) * 2

    const totalPriceMonthly = values.totalPriceMonthly ?? 0
    const initialPayment = values.initialPayment ?? 0
    const averageMonthlyPrice = Number((totalPriceMonthly / 12).toFixed(2))
    const monthlyInstallment =
        values.totalPriceMonthly != null && values.initialPayment != null
            ? Number(((totalPriceMonthly - initialPayment) / 11).toFixed(2))
            : averageMonthlyPrice

    const hasValidTotalPriceMonthly = totalPriceMonthly > 0

    const onTotalPriceYearlyChange = () => {
        formik.setFieldError('totalPriceYearly', undefined)
    }

    const onTotalPriceMonthlyChange = (newTotalPriceMonthly: number) => {
        formik.setFieldValue('totalPriceMonthly', newTotalPriceMonthly)
        formik.setFieldValue('initialPayment', Number((newTotalPriceMonthly / 12).toFixed(2)) || null)
        formik.setFieldError('totalPriceMonthly', undefined)
        formik.setFieldError('initialPayment', undefined)
    }

    const onMonthlyInstallmentChange = (newMonthlyPrice: number) => {
        const newInitialPayment = Math.max(totalPriceMonthly - newMonthlyPrice * 11, newMonthlyPrice).toFixed(2)
        formik.setFieldValue('initialPayment', newInitialPayment || null)
        formik.setFieldError('initialPayment', undefined)
    }

    useEffect(onTotalPriceYearlyChange, [values.totalPriceYearly])

    useEffect(() => {
        formik.setFieldError('pricePerYear', undefined)
    }, [values.pricePerYear])

    useEffect(() => {
        formik.setFieldError('priceUpfront', undefined)
    }, [values.priceUpfront])

    useEffect(() => {
        if (hasValidTotalPriceMonthly) {
            onTotalPriceMonthlyChange(totalPriceMonthly)
        }
    }, [totalPriceMonthly])

    const canGoNext = isTwoYearTerm
        ? values.allowPerYearPayment || values.allowUpfrontPayment
        : values.allowYearlyInstallments || values.allowMonthlyInstallments

    return (
        <div className={classNames('form-step pricing-plan-installments', { visible })}>
            <ModalBody>
                {activePricingPlan && (
                    <div className="current-plan-reference">
                        <label className="reference-header">Current active plan</label>
                        <PricingPlanElement pricingPlan={activePricingPlan} readOnly />
                    </div>
                )}
                {showContractLength && (
                    <ContractLength
                        minimumTerm={values.minimumTerm}
                        onTermChange={(term) => formik.setFieldValue('minimumTerm', term)}
                    />
                )}
                <div
                    className={classNames('collapsible term-options', { expanded: !isTwoYearTerm })}
                    {...({ inert: isTwoYearTerm || undefined } as any)}
                >
                    <div className="collapsible-content">
                        <AnnualPlan
                            allowYearlyInstallments={values.allowYearlyInstallments}
                            suggestedPrice={suggestedTotalPriceYearly}
                        />
                        <MonthlyPlan
                            allowMonthlyInstallments={values.allowMonthlyInstallments}
                            suggestedPrice={suggestedTotalPriceMonthly}
                            totalPriceMonthly={totalPriceMonthly}
                            averageMonthlyPrice={averageMonthlyPrice}
                            initialPayment={initialPayment}
                            monthlyInstallment={monthlyInstallment}
                            hasValidTotalPriceMonthly={hasValidTotalPriceMonthly}
                            onInitialPaymentChange={(value) => formik.setFieldValue('initialPayment', value)}
                            onMonthlyInstallmentChange={onMonthlyInstallmentChange}
                            errors={formik.errors}
                        />
                    </div>
                </div>
                <div
                    className={classNames('collapsible term-options', { expanded: isTwoYearTerm })}
                    {...({ inert: !isTwoYearTerm || undefined } as any)}
                >
                    <div className="collapsible-content">
                        <PerYearPlan
                            allowPerYearPayment={values.allowPerYearPayment}
                            suggestedPrice={suggestedTotalPriceYearly}
                        />
                        <UpfrontPlan
                            allowUpfrontPayment={values.allowUpfrontPayment}
                            suggestedPrice={suggestedPriceUpfront}
                        />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <CancelButton secondary size="large" onClick={onClose}>
                    Close
                </CancelButton>
                <ActionButton size="large" onClick={onNextClick} disabled={!canGoNext}>
                    Next
                </ActionButton>
            </ModalFooter>
        </div>
    )
}
